import { AxiosResponse } from "axios";

import {
  useIssue,
  useIssueRatingCategories,
  useIssueRatings,
  useMe,
  useUpdateAttachments,
} from "hooks";
import { useCallback, useMemo } from "react";
import { useMutation, UseMutationOptions, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  IAISimilar,
  IAISuggestions,
  IAsset,
  IAttachment,
  IIssue,
  IIssueRating,
} from "types";
import { IManagementProps, IValues, IssueQualityReviewMap } from "./IssueForm.types";
import {
  createIssue,
  updateIssue,
  bulkUpdateOrCreateIssueRatings,
} from "./IssueForm.services";
import { IssueInitial, IssuePayload, ISSUE_FIELD_NAMES } from "./IssueForm.constants";
import { FormikHelpers } from "formik";
import { CONTENT_QUALITY_LEVELS, ENDPOINTS } from "const";
import { showErrorMessage, showSuccessMessage } from "utils";
import {
  createAISimilarIssues,
  createAISuggestionsIssues,
  IAIIssueProps,
} from "services";

export function useInitialState() {
  const { data: meData } = useMe();
  const { id } = useParams<{ id: string }>();
  const { data: categoriesData } = useIssueRatingCategories();
  const { data: issueRate } = useIssueRatings({
    userId: meData?.data.id,
    issueId: id,
    enabled: !!id,
  });
  const { data: issueData } = useIssue({ id });
  return useMemo(() => {
    return new IssueInitial(
      issueData?.data,
      categoriesData?.data,
      issueRate?.data?.items
    );
  }, [issueData?.data, categoriesData?.data, issueRate?.data?.items]);
}

interface IAIAnalyticsIssueProps
  extends Partial<UseMutationOptions<AxiosResponse<IAISuggestions>, any, any>> {
  id?: string | undefined;
}

export function useCreateAIAnalyticsIssue({ ...rest }: IAIAnalyticsIssueProps) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<IAISuggestions>, any, any>(
    (issue: IAIIssueProps) => createAISuggestionsIssues({ ...issue }),
    {
      mutationKey: ["createAIAnalyticsIssue"],
      onSuccess: (data) => {
        queryClient.setQueryData(["createAIAnalyticsIssue"], data.data || {});
      },

      ...rest,
    }
  );
}

interface IAISimilarIssueProps
  extends Partial<UseMutationOptions<AxiosResponse<IAISimilar>, any, any>> {
  id?: string | undefined;
}

export function useCreateAISimilarIssue({ ...rest }: IAISimilarIssueProps) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<IAISimilar>, any, any>(
    (issue: IAIIssueProps) => createAISimilarIssues({ ...issue }),
    {
      mutationKey: ["createAISimilarIssue"],
      onSuccess: (data) => {
        queryClient.setQueryData(["createAISimilarIssue"], data.data || {});
      },

      ...rest,
    }
  );
}

interface IIssueProps
  extends Partial<UseMutationOptions<AxiosResponse<IIssue>, any, any>> {
  id?: string | undefined;
}

export function useCreateIssue({ ...rest }: IIssueProps) {
  return useMutation<AxiosResponse<IIssue>, any, any>(
    (issue: IIssue) => createIssue({ issue }),
    {
      ...rest,
    }
  );
}

export function useUpdateIssue({
  id = "",
  ...rest
}: IIssueProps & {
  id: string | undefined;
}) {
  return useMutation<AxiosResponse<IIssue>, any, any>(
    (issue: IIssue) => updateIssue({ id, issue }),
    {
      ...rest,
    }
  );
}
interface IIssueRatingProps
  extends Partial<
    UseMutationOptions<AxiosResponse<Partial<IIssueRating>[]>, any, any>
  > {}
export function useUpdateOrCreateIssueRating({
  ...rest
}: IIssueRatingProps = {}) {
  return useMutation<AxiosResponse<Partial<IIssueRating>[]>, any, any>(
    async ({
      issueRating,
      issueId,
    }: {
      issueRating: ReturnType<IssuePayload["getIssueRatings"]>;
      issueId: string;
    }) => {
      return bulkUpdateOrCreateIssueRatings(issueRating, issueId);
    },
    {
      ...rest,
    }
  );
}

///
export function useSubmitHandler(
  success: (id?: string) => void,
  manage: IManagementProps,
  scrollSlideoverToTop?: () => void
) {
  const params = useParams<{ id: string }>();

  const { mutateAsync: mutateCreateAISuggestionIssueAsync } =
    useCreateAIAnalyticsIssue({});
  const { mutateAsync: mutateCreateAISimilarIssueAsync } =
    useCreateAISimilarIssue({});
  const { mutateAsync: mutateCreateIssueAsync } = useCreateIssue({});
  const { mutateAsync: mutateUpdateIssueAsync } = useUpdateIssue({
    id: params.id,
  });
  const { mutateAsync: mutateUpdateOrCreateIssueRating } =
    useUpdateOrCreateIssueRating();
  const { data: categories } = useIssueRatingCategories();
  const { mutateAsync: updateAttachments } = useUpdateAttachments();

  const queryClient = useQueryClient();

  const qualityTrackedFields = useMemo(
    () => [
      ISSUE_FIELD_NAMES.TITLE,
      ISSUE_FIELD_NAMES.DETAILS,
      ISSUE_FIELD_NAMES.ORIGIN,
      ISSUE_FIELD_NAMES.IMPORTANCE,
    ],
    []
  );
  const qualityTrackedFieldSet = useMemo(
    () =>
      new Set(
        qualityTrackedFields.map((field) => field.toLocaleLowerCase())
      ),
    [qualityTrackedFields]
  );

  const normalize = useCallback((value?: string | null) => {
    if (!value) {
      return "";
    }
    const unicodeNormalized =
      typeof value.normalize === "function" ? value.normalize("NFKC") : value;
    return unicodeNormalized
      .trim()
      .replace(/\s+/g, " ")
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
      .replace(/[\u2013\u2014\u2212]/g, "-")
      .replace(/([!?.,])\1+/g, "$1")
      .replace(/[!?.,]+$/g, "")
      .toLocaleLowerCase();
  }, []);

  const levenshteinDistance = useCallback((a: string, b: string) => {
    if (a === b) {
      return 0;
    }
    if (!a.length) {
      return b.length;
    }
    if (!b.length) {
      return a.length;
    }

    const lenB = b.length;
    const prev = Array.from({ length: lenB + 1 }, (_, index) => index);
    const curr = new Array<number>(lenB + 1);

    for (let i = 1; i <= a.length; i++) {
      curr[0] = i;
      const charA = a[i - 1];
      for (let j = 1; j <= lenB; j++) {
        const cost = charA === b[j - 1] ? 0 : 1;
        const insertion = curr[j - 1] + 1;
        const deletion = prev[j] + 1;
        const substitution = prev[j - 1] + cost;
        curr[j] = Math.min(insertion, deletion, substitution);
      }
      for (let j = 0; j <= lenB; j++) {
        prev[j] = curr[j];
      }
    }
    return prev[lenB];
  }, []);

  const isMinorEdit = useCallback(
    (previous: string, current: string) => {
      if (!previous || !current) {
        return false;
      }
      const maxEditDistance = 3;
      if (Math.abs(previous.length - current.length) > maxEditDistance) {
        return false;
      }
      const distance = levenshteinDistance(previous, current);
      if (distance > maxEditDistance) {
        return false;
      }
      const relativeChange = distance / Math.max(previous.length, current.length);
      return relativeChange <= 0.2;
    },
    [levenshteinDistance]
  );

  return useCallback(
    async (values: IValues, formikHelpers: FormikHelpers<IValues>) => {
      formikHelpers.setSubmitting(true);
      const invalidate = () => {
        queryClient.invalidateQueries([ENDPOINTS.ISSUES]);
        queryClient.resetQueries([ENDPOINTS.ISSUES]);
        queryClient.invalidateQueries([ENDPOINTS.ISSUE]);
        queryClient.invalidateQueries([ENDPOINTS.MAP_LOCATIONS]);
        queryClient.invalidateQueries([ENDPOINTS.ISSUE_RATING]);
      };
      const issue = new IssuePayload(
        { ...values, attachmentsType: "issues" },
        categories?.data
      );
      try {
        let issueId = params.id;

        const ratingsPayload: Record<string, number> = {};
        categories?.data?.forEach((category) => {
          ratingsPayload[category.category] = Number(
            (values as Record<string, number | undefined>)[category.category] ?? 0
          );
        });

        const docUrls =
          values?.attachments
            ?.filter((a) => a.type === "file")
            ?.map((a) => (a as unknown as IAttachment<IAsset>)?.asset?.url)
            ?.filter((url): url is string => !!url) || [];

        const linkUrls =
          values?.attachments
            ?.filter((a) => a.type === "link")
            ?.map((a) => (a as unknown as IAttachment)?.data)
            ?.filter((link): link is string => !!link) || [];

        const labelValues = values?.tags?.filter((tag) => !!tag) || [];

        const issueAnalyticsPayload: IAIIssueProps = {
          issue_title: values.title || "",
          issue_location:
            values.location?.displayLabel || values.location?.label || "",
          issue_details: values.details || "",
          issue_origin: values.origin || "",
          issue_importance: values.importance || "",
          ratings: ratingsPayload,
        };

        if (docUrls.length > 0) {
          issueAnalyticsPayload.docs = docUrls;
        }
        if (linkUrls.length > 0) {
          issueAnalyticsPayload.links = linkUrls;
        }
        if (labelValues.length > 0) {
          issueAnalyticsPayload.labels = labelValues;
        }

        const qualityCache = manage.qualityReviewRef?.current;

        const shouldEvaluateField = (field: ISSUE_FIELD_NAMES) => {
          if (!qualityCache) {
            return true;
          }
          const cached = qualityCache[field];
          if (!cached) {
            return true;
          }
          const isHighQuality =
            cached.quality === CONTENT_QUALITY_LEVELS.HIGH ||
            cached.quality === CONTENT_QUALITY_LEVELS.GOOD;
          if (!isHighQuality) {
            return true;
          }
          const currentValue = (values[field] as string) || "";
          const cachedNormalized = normalize(cached.user_input);
          const currentNormalized = normalize(currentValue);

          if (cachedNormalized === currentNormalized) {
            return false;
          }

          if (isMinorEdit(cachedNormalized, currentNormalized)) {
            return false;
          }

          return true;
        };

        const fieldsToEvaluate = qualityTrackedFields.filter((field) =>
          shouldEvaluateField(field)
        );
        const fieldsToEvaluateSet = new Set(
          fieldsToEvaluate.map((field) => field.toLocaleLowerCase())
        );

        const requireAISuggestions =
          manage?.proceedWithSuggestions !== true &&
          fieldsToEvaluate.length > 0;

        let aiSuggestionsReviewResult: Pick<AxiosResponse<IAISuggestions>, "data">;

        if (manage?.proceedWithSuggestions === true) {
          aiSuggestionsReviewResult = {
            data: {
              entity: "issue",
              feedback_areas: [],
              more_feedback: {
                quality: CONTENT_QUALITY_LEVELS.HIGH,
                feedback: "",
              },
            },
          };
        } else if (!requireAISuggestions) {
          aiSuggestionsReviewResult = {
            data: {
              entity: "issue",
              feedback_areas: qualityCache
                ? Object.values(qualityCache)
                : [],
              more_feedback: {
                quality: CONTENT_QUALITY_LEVELS.HIGH,
                feedback: "",
              },
            },
          };
        } else {
          const aiResponse = await mutateCreateAISuggestionIssueAsync(
            issueAnalyticsPayload
          );
          aiSuggestionsReviewResult = { data: aiResponse.data };
        }

        const feedbackAreas =
          aiSuggestionsReviewResult.data.feedback_areas || [];
        const trackedFeedbackAreas = feedbackAreas.filter((area) =>
          qualityTrackedFieldSet.has(area.input_name.toLocaleLowerCase())
        );

        const baseCache: IssueQualityReviewMap = qualityCache
          ? { ...qualityCache }
          : {};
        const nextQualityCache: IssueQualityReviewMap = { ...baseCache };

        trackedFeedbackAreas.forEach((area) => {
          const key = area.input_name.toLocaleLowerCase();
          if (fieldsToEvaluateSet.has(key) || !nextQualityCache[key]) {
            nextQualityCache[key] = area;
          }
        });

        const mergedFeedbackAreas = qualityTrackedFields
          .map((field) => {
            const key = field.toLocaleLowerCase();
            return nextQualityCache[key];
          })
          .filter((area): area is NonNullable<typeof area> => !!area);

        if (manage.qualityReviewRef) {
          manage.qualityReviewRef.current = nextQualityCache;
        }

        const sanitizedResult: IAISuggestions = {
          ...aiSuggestionsReviewResult.data,
          feedback_areas:
            mergedFeedbackAreas.length > 0
              ? mergedFeedbackAreas
              : trackedFeedbackAreas,
        };

        formikHelpers.setFieldValue("suggestedResult", sanitizedResult);

        const hasBlockingAreas = trackedFeedbackAreas.some(
          (area) =>
            area.quality !== CONTENT_QUALITY_LEVELS.GOOD &&
            area.quality !== CONTENT_QUALITY_LEVELS.HIGH
        );
        const moreFeedbackQuality = sanitizedResult?.more_feedback?.quality;
        const hasBlockingMoreFeedback =
          moreFeedbackQuality === CONTENT_QUALITY_LEVELS.POOR ||
          moreFeedbackQuality === CONTENT_QUALITY_LEVELS.USELESS;

        if (
          manage?.proceedWithSuggestions === false &&
          (hasBlockingAreas || hasBlockingMoreFeedback)
        ) {
          formikHelpers.setSubmitting(false);
          formikHelpers.setTouched({
            title: false,
            details: false,
            origin: false,
            importance: false,
          });
          document.querySelector("#issue-report")?.scrollTo(0, 0);
          return;
        }

        if (manage?.proceedWithSimilar === true) {
          await mutateCreateAISimilarIssueAsync(issueAnalyticsPayload);
        }

        if (issueId && manage.showIssueFields) {
          await mutateUpdateIssueAsync(issue.getIssue());
        }
        if (!issueId && manage.showIssueFields) {
          const created = await mutateCreateIssueAsync(issue.getIssue());
          issueId = created.data.id;
        }
        if (issueId && manage.showIssueFields) {
          await updateAttachments(issue.getAttachments(issueId));
        }
        if (issueId && manage.showRatings) {
          await mutateUpdateOrCreateIssueRating({
            issueRating: issue.getIssueRatings(issueId),
            issueId, //add issue dynamically here, because on creating issue we don't have issueId
          });
          showSuccessMessage("Success!");
          showSuccessMessage("Rating is updated!");
        }
        if (manage.qualityReviewRef) {
          manage.qualityReviewRef.current = {};
        }
        formikHelpers.setFieldValue("suggestedResult", undefined);
        success(issueId);
        invalidate();
        formikHelpers.setSubmitting(false);
        queryClient.getMutationCache().clear();
      } catch (error) {
        formikHelpers.setSubmitting(false);

        if (error?.response?.data) {
          formikHelpers.setErrors(error.response.data);
        }

        showErrorMessage("Error occurred! Check form!");
      }
    },
    [
      categories?.data,
      isMinorEdit,
      manage?.proceedWithSimilar,
      manage?.proceedWithSuggestions,
      manage?.qualityReviewRef,
      manage.showIssueFields,
      manage.showRatings,
      mutateCreateAISimilarIssueAsync,
      mutateCreateAISuggestionIssueAsync,
      mutateCreateIssueAsync,
      mutateUpdateIssueAsync,
      mutateUpdateOrCreateIssueRating,
      normalize,
      params.id,
      queryClient,
      qualityTrackedFieldSet,
      qualityTrackedFields,
      success,
      updateAttachments,
    ]
  );
}
